// Мастер «Новый сотрудник». Панель, а не модалка: заведение человека обратимо, лишняя
// карточка увольняется, а не остаётся навсегда. Мастер не уводит из раздела: список слева
// остаётся на экране, адрес панели живёт в ?p=, и ссылка «раздел с открытым мастером»
// открывается у коллеги ровно так же.
//
// ## Проверка на дубль
//
// Идёт ДВАЖДЫ и в двух разных смыслах.
//
// 1. **До сохранения**, по расфокусу телефона и паспорта. Человек ещё не нажал «Создать», и
//    ему показывается ссылка на найденную карточку: возможно, он сейчас заводит того, кто
//    уже есть. Именно расфокус, а не каждая буква: номер набирается одиннадцатью нажатиями,
//    и десять из одиннадцати запросов ушли бы по недонабранному номеру.
// 2. **После сохранения** — сервер возвращает `duplicate_warning` вместе с уже созданной
//    карточкой. Это не ошибка: у заказчика есть полные тёзки, запрет по совпадению телефона
//    заблокировал бы заведение настоящего второго человека. Предупреждение переезжает в
//    карточку и показывается там.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiErrorWarningLine, RiPhoneLine, RiUser3Line } from '@remixicon/react';
import { useLocation, useNavigate } from 'react-router';

import type { ApiError } from '@/shared/errors/api-error';
import type { EmployeeIn } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { useEmploymentTypesQuery } from '@/shared/org/queries';
import { can } from '@/shared/rbac/permissions';
import { parseSearch, popPanel } from '@/shared/routing/search';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { DateField } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import { DuplicateNotice } from '../components/DuplicateNotice';
import { newIdempotencyKey, useCreateEmployeeMutation, useDuplicatesQuery } from '../queries';
import { DEFAULT_VIEW, FILTER_STATUS, peopleHref, setFilter } from '../route';

/** Заведение и правка людей: server/app/api/v1/employees.py. */
const EDIT_PERMISSION = 'core.employee.edit';

type FieldName = 'last_name' | 'first_name' | 'phone' | 'passport';

export function NewEmployeePanel({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = useAuthStore((state) => state.permissions);
  const allowed = can(permissions, EDIT_PERMISSION);

  const [idempotencyKey] = useState(newIdempotencyKey);

  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [employmentTypeId, setEmploymentTypeId] = useState(NO_VALUE);
  const [hiredAt, setHiredAt] = useState('');
  const [passportSeries, setPassportSeries] = useState('');
  const [passportNumber, setPassportNumber] = useState('');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [gateNote, setGateNote] = useState<string | null>(null);

  // Что уже отдали на проверку. Значение меняется ТОЛЬКО по расфокусу — этим и задаётся
  // момент запроса, без таймеров и без запроса на каждую букву.
  const [checked, setChecked] = useState<{ phone: string; series: string; number: string }>({
    phone: '',
    series: '',
    number: '',
  });

  const employmentTypes = useEmploymentTypesQuery();
  const duplicates = useDuplicatesQuery(
    {
      phone: checked.phone === '' ? undefined : checked.phone,
      passport_series: checked.series === '' ? undefined : checked.series,
      passport_number: checked.number === '' ? undefined : checked.number,
    },
    checked.phone !== '' || (checked.series !== '' && checked.number !== ''),
  );
  const mutation = useCreateEmployeeMutation(idempotencyKey);

  function runDuplicateCheck() {
    setChecked({
      phone: phone.trim(),
      series: passportSeries.trim(),
      number: passportNumber.trim(),
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setGateNote(
        'Заводить людей может тот, у кого есть право на правку карточек. Попросите администратора выдать его — форма при этом никуда не денется.',
      );
      return;
    }
    if (mutation.isPending) {
      setGateNote('Карточка уже создаётся, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }

    const next: Partial<Record<FieldName, string>> = {};
    if (lastName.trim() === '') {
      next.last_name = 'Введите фамилию — по ней человека будут искать в реестре.';
    }
    if (firstName.trim() === '') {
      next.first_name = 'Введите имя: в ведомости человек подписывается фамилией и именем.';
    }
    const hasSeries = passportSeries.trim() !== '';
    const hasNumber = passportNumber.trim() !== '';
    if (hasSeries !== hasNumber) {
      next.passport =
        'Паспорт заводится целиком: серия и номер вместе. По половине проверка на дубль не сработает.';
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setErrors({});
    setGateNote(null);
    const body: EmployeeIn = {
      last_name: lastName.trim(),
      first_name: firstName.trim(),
      middle_name: middleName.trim() === '' ? null : middleName.trim(),
      phone: phone.trim() === '' ? null : phone.trim(),
      city: city.trim() === '' ? null : city.trim(),
      employment_type_id: employmentTypeId === NO_VALUE ? null : employmentTypeId,
      hired_at: hiredAt === '' ? null : hiredAt,
      passport_series: hasSeries ? passportSeries.trim() : null,
      passport_number: hasNumber ? passportNumber.trim() : null,
    };
    mutation.mutate(body, {
      onSuccess: (employee) => {
        // Панель уходит, карточка нового человека открывается сразу: он видит результат
        // своего действия, а не возвращается в список искать его глазами. Предупреждение о
        // дубле уже лежит в кэше и покажется в карточке.
        //
        // Список при этом открывается на вкладке, где человек теперь ЖИВЁТ: новый сотрудник
        // заводится «в резерве», а реестр по умолчанию показывает «На вахте». Без этого строка
        // выбранной карточки не нашлась бы ни в одной видимой вкладке, и человек решил бы, что
        // только что заведённый пропал. Состояние берётся из ответа сервера, а не из головы:
        // куда сервер его положил, там и смотрим.
        const search = popPanel(parseSearch(location.search));
        navigate(
          peopleHref(
            DEFAULT_VIEW,
            setFilter({ ...search, tab: undefined }, FILTER_STATUS, employee.status),
            employee.id,
          ),
        );
      },
    });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3'>
      {!allowed && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку карточек у вас нет: форму можно заполнить, но сохранить человека не
            выйдет. Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      <TextField
        id='new-employee-last-name'
        label='Фамилия'
        required
        icon={RiUser3Line}
        value={lastName}
        onChange={setLastName}
        placeholder='Старцев'
        error={errors.last_name}
      />
      <TextField
        id='new-employee-first-name'
        label='Имя'
        required
        value={firstName}
        onChange={setFirstName}
        placeholder='Иван'
        error={errors.first_name}
      />
      <TextField
        id='new-employee-middle-name'
        label='Отчество'
        value={middleName}
        onChange={setMiddleName}
        placeholder='Петрович'
        hint='Необязательно: у части людей отчества нет в документах.'
      />

      <TextField
        id='new-employee-phone'
        label='Телефон'
        icon={RiPhoneLine}
        type='tel'
        inputMode='tel'
        value={phone}
        onChange={setPhone}
        onBlur={runDuplicateCheck}
        placeholder='+7 900 123-45-67'
        hint='Пишите как удобно: сервер приведёт номер к единому виду и проверит, нет ли такого человека.'
      />

      <TextField
        id='new-employee-city'
        label='Город'
        value={city}
        onChange={setCity}
        placeholder='Пыть-Ях'
        hint='Город человека, а не объекта: по нему считают дорогу на вахту.'
      />

      <SelectField
        id='new-employee-type'
        label='Тип оформления'
        value={employmentTypeId}
        onChange={setEmploymentTypeId}
        options={(employmentTypes.data?.items ?? []).map((item) => ({
          value: item.id,
          label: item.title,
        }))}
        noneLabel='Тип не задан'
        placeholder='Выберите тип оформления'
        pending={employmentTypes.isPending}
        failed={employmentTypes.isError}
        failedError={employmentTypes.error as ApiError | null}
        emptyNote='Типов оформления в справочнике нет. Их заводит администратор в настройках; без типа человек не попадёт ни в одно представление реестра по оформлению.'
        hint='Штат, договор, самозанятый — от типа зависит представление в реестре.'
      />

      <DateField
        id='new-employee-hired-at'
        label='Дата приёма'
        value={hiredAt}
        onChange={setHiredAt}
        hint='С неё считается стаж и первая вахта. Можно заполнить позже.'
      />

      <div className='grid grid-cols-2 gap-2'>
        <TextField
          id='new-employee-passport-series'
          label='Серия паспорта'
          inputMode='numeric'
          maxLength={16}
          value={passportSeries}
          onChange={setPassportSeries}
          onBlur={runDuplicateCheck}
          placeholder='6714'
        />
        <TextField
          id='new-employee-passport-number'
          label='Номер паспорта'
          inputMode='numeric'
          maxLength={32}
          value={passportNumber}
          onChange={setPassportNumber}
          onBlur={runDuplicateCheck}
          placeholder='123456'
        />
      </div>
      {errors.passport !== undefined ? (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {errors.passport}
        </Hint.Root>
      ) : (
        <Hint.Root>
          Паспорт хранится зашифрованным, наружу уходят только последние четыре знака. Нужен он для
          проверки на дубль.
        </Hint.Root>
      )}

      {duplicates.data !== undefined && (
        <DuplicateNotice
          report={duplicates.data}
          note='Это предупреждение, а не запрет: у заказчика встречаются полные тёзки. Откройте найденную карточку и решите сами — заводить нового или работать с прежним.'
        />
      )}

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {gateNote !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {gateNote}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Создаём карточку…' : 'Создать сотрудника'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
